/* eslint-disable no-param-reassign,no-restricted-syntax */
import { v4 as uuidv4 } from 'uuid';
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  ActivateMembershipTierMutationVariables,
  ChatGroup,
  ContentEntry,
  CreateContentEntryInput,
  CreateMealInput,
  CreateMembershipTierInput,
  CreateNoteInput,
  CreatePlannerWeekInput,
  CreateProductsBoardInput,
  CreateProgrammeInput,
  CreateSharedBoardInput,
  CreateSharedContentBoardInput,
  CreateSharedContentEntryInput,
  CreateSharedMealInput,
  CreateSharedProgrammeInput,
  CreateShoppingListInput,
  CreateSmorgBoardInput,
  CreateSpaceInput,
  CreateSpacePrivateConfigInput,
  CreateUserMyDayActionRecordInput,
  CreateUserProfileInput,
  CreateUserProgrammeEnrollmentInput,
  GetGRCResultsMutationVariables,
  GlobalTag,
  GRCRecipe,
  GRCRecipeCategory,
  GRCResultPage,
  Meal,
  MembershipTier,
  Note,
  PlannerWeek,
  ProductsBoard,
  Programme,
  RecipeIngredient,
  RecipePreparation,
  SharedBoard,
  SharedContentBoard,
  SharedContentEntry,
  SharedMeal,
  SharedProgramme,
  ShoppingList,
  SmorgBoard,
  Space,
  SpaceMembership,
  SpacePrivateConfig,
  SyncSpaceMembershipMutationVariables,
  UpdateContentEntryInput,
  UpdateMealInput,
  UpdateMembershipTierInput,
  UpdateNoteInput,
  UpdatePlannerWeekInput,
  UpdateProductsBoardInput,
  UpdateProgrammeInput,
  UpdateSharedBoardInput,
  UpdateSharedContentBoardInput,
  UpdateSharedContentEntryInput,
  UpdateSharedProgrammeInput,
  UpdateShoppingListInput,
  UpdateSmorgBoardInput,
  UpdateSpaceInput,
  UpdateSpacePrivateConfigInput,
  UpdateUserProfileInput,
  UpdateUserProgrammeEnrollmentInput,
  UserMyDayActionRecord,
  UserProfile,
  UserProgrammeEnrollment,
} from '../API';
import { graphqlBaseQuery, QueryType } from './appSyncGraphQLApi';
import { getInputForUpdate } from '../operations/utils';
import { objectify, objectifyArrayOfArrays } from '../services/typed_arrays';
import { distilledMealFromMeal } from '../services/meals';
import { persistEndUserDesiredSpaceID } from '../features/white_label/white_label_slice';
import { userIsCreator } from '../services/users';

/**
 * The end-user app AppSync GraphQL API
 */

export interface IDAndParentID {
  id: string;
  parentID: string;
}

export interface OwnerAndGroups {
  username: string;
  groups: Array<string>;
}

export const appSyncGraphQLApi = createApi({
  reducerPath: 'appSyncGraphQLApi',
  baseQuery: graphqlBaseQuery(),
  keepUnusedDataFor: 10 * 60,
  tagTypes: [
    'UserProfile',
    'SpaceMembership',
    'SmorgBoard',
    'HealthProBoard',
    'DbPlannerWeekByStartDate',
    'DbPlannerWeekByID',
    'DistilledMealsByParent',
    'FullMeal',
    'FullMealsByParent',
    'NotesByParent',
    'ContentEntriesByParent',
    'UserMyDayActionRecordBySpaceMembership',
    'UserMyDayActionRecordByEnrollment',
    'MembershipTier',
    'Space',
    'ProductsBoard',
    'SharedContentEntriesByParent',
    'SpacePrivateConfig',
    'ShoppingList',
    'UserProgrammeEnrollment',
    'Programme',
    'SharedContentBoard',
    'SharedProgramme',
    'SharedBoard',
    'DistilledSharedMealsByParent',
  ],
  endpoints: (builder) => ({
    listUserProfile: builder.query<UserProfile, string>({
      query: (userID) => ({
        queryName: 'userProfileByUserID',
        data: { userID },
        queryType: QueryType.SCAN,
      }),
      transformResponse: (response) => response[0],
      providesTags: ['UserProfile'],
      onQueryStarted: async (userID, { queryFulfilled }) => {
        const { data: userProfile } = await queryFulfilled;
        if (userProfile && userIsCreator(userProfile)) {
          persistEndUserDesiredSpaceID(null);
        }
      },
    }),
    getSpaceMembershipsByMemberID: builder.query<
      Array<SpaceMembership>,
      string
    >({
      query: (userID) => ({
        queryName: 'spaceMembershipByMemberID',
        data: { memberID: userID },
        queryType: QueryType.SCAN,
      }),
      providesTags: (
        result,
      ): ({ type: 'SpaceMembership'; id: string } | undefined)[] => [
        {
          type: 'SpaceMembership' as const,
          id: 'LIST',
        },
        ...(result || []).map((spaceMembership) => ({
          type: 'SpaceMembership' as const,
          id: spaceMembership.id,
        })),
      ],
    }),
    getDbPlannerWeek: builder.query<PlannerWeek, string>({
      query: (startDate) => ({
        queryName: 'plannerWeekByStartDate',
        data: { startDate },
        queryType: QueryType.SCAN,
      }),
      transformResponse: (response) => response[0],
      providesTags: (result, error, startDate) => [
        { type: 'DbPlannerWeekByStartDate', id: startDate },
      ],
    }),
    getDbPlannerWeekByID: builder.query<PlannerWeek, string>({
      query: (id) => ({
        queryName: 'getPlannerWeek',
        data: { id },
        queryType: QueryType.QUERY_BY_ID,
      }),
      providesTags: (result, error, id) => [{ type: 'DbPlannerWeekByID', id }],
    }),
    getDistilledMealsByParent: builder.query<Record<string, Meal>, string>({
      query: (parentID) => ({
        queryName: 'listDistilledMealsByParent',
        data: { smorgBoardID: parentID },
        queryType: QueryType.SCAN,
        appSyncParams: { responseDataKey: 'mealBySmorgBoard' },
      }),
      transformResponse: objectify,
      providesTags: (result, error, parentID) => [
        { type: 'DistilledMealsByParent', id: parentID },
      ],
    }),
    getFullMealsByParent: builder.query<Record<string, Meal>, string>({
      query: (parentID) => ({
        queryName: 'mealBySmorgBoard',
        data: { smorgBoardID: parentID },
        queryType: QueryType.SCAN,
      }),
      transformResponse: objectify,
      providesTags: (result, error, parentID) => [
        { type: 'FullMealsByParent', id: parentID },
      ],
    }),
    getFullMeal: builder.query<Meal, string>({
      query: (id) => ({
        queryName: 'getMeal',
        data: { id },
        queryType: QueryType.QUERY_BY_ID,
      }),
      providesTags: (result, error, id) => [{ type: 'FullMeal', id }],
    }),
    getNotesByParent: builder.query<Record<string, Note>, string>({
      query: (parentID) => ({
        queryName: 'noteByParent',
        data: { parentID },
        queryType: QueryType.SCAN,
      }),
      transformResponse: objectify,
      providesTags: (result, error, parentID) => [
        { type: 'NotesByParent', id: parentID },
      ],
    }),
    getContentEntriesByParent: builder.query<
      Record<string, ContentEntry>,
      string
    >({
      query: (parentID) => ({
        queryName: 'contentEntryByParent',
        data: { parentID },
        queryType: QueryType.SCAN,
      }),
      transformResponse: objectify,
      providesTags: (result, error, parentID) => [
        { type: 'ContentEntriesByParent', id: parentID },
      ],
    }),
    getContentEntry: builder.query<ContentEntry, string>({
      query: (id) => ({
        queryName: 'getContentEntry',
        data: { id },
        queryType: QueryType.QUERY_BY_ID,
      }),
    }),
    getUserProgrammeEnrollments: builder.query<
      Array<UserProgrammeEnrollment>,
      void
    >({
      query: () => ({
        queryName: 'listUserProgrammeEnrollments',
        data: {},
        queryType: QueryType.SCAN,
      }),
      providesTags: [{ type: 'UserProgrammeEnrollment', id: 'LIST' }],
    }),
    getSharedProgramme: builder.query<SharedProgramme, string>({
      query: (id) => ({
        queryName: 'getSharedProgramme',
        data: { id },
        queryType: QueryType.QUERY_BY_ID,
      }),
      providesTags: (result, error, id) => [{ type: 'SharedProgramme', id }],
    }),
    getPublicSpace: builder.query<Space, string>({
      query: (id) => ({
        queryName: 'getPublicSpace',
        data: { id },
        queryType: QueryType.QUERY_BY_ID,
        appSyncParams: { authMode: 'iam', responseDataKey: 'getSpace' },
      }),
      providesTags: [{ type: 'Space', id: 'LIST' }],
      onQueryStarted: async (id, { queryFulfilled }) => {
        const { data: space } = await queryFulfilled;
        if (space) {
          persistEndUserDesiredSpaceID(space.id);
        }
      },
    }),
    getSpace: builder.query<Space, void>({
      query: () => ({
        queryName: 'listSpaces',
        data: {},
        queryType: QueryType.SCAN,
      }),
      transformResponse: (response) => response[0],
      providesTags: [{ type: 'Space', id: 'LIST' }],
    }),
    getSmorgBoard: builder.query<SmorgBoard, string>({
      query: (id) => ({
        queryName: 'getSmorgBoard',
        data: { id },
        queryType: QueryType.QUERY_BY_ID,
      }),
      providesTags: (result, error, id) => [
        { type: 'SmorgBoard', id },
        { type: 'HealthProBoard', id },
      ],
    }),
    getSharedBoard: builder.query<SharedBoard, string>({
      query: (id) => ({
        queryName: 'getSharedBoard',
        data: { id },
        queryType: QueryType.QUERY_BY_ID,
      }),
      providesTags: (result, error, id) => [{ type: 'SharedBoard', id }],
    }),
    getSharedBoardsByIDs: builder.query<Array<SharedBoard>, Array<string>>({
      query: (ids) => ({
        queryName: 'getSharedBoard',
        data: { id: ids },
        queryType: QueryType.QUERY_BY_IDS,
      }),
      providesTags: (result) =>
        (result || []).map((sharedBoard) => ({
          type: 'SharedBoard',
          id: sharedBoard.id,
        })),
    }),
    getDistilledSharedMealsByParent: builder.query<
      Record<string, SharedMeal>,
      string
    >({
      query: (parentID) => ({
        queryName: 'listDistilledSharedMealsByParent',
        data: { sharedBoardID: parentID },
        queryType: QueryType.SCAN,
        appSyncParams: { responseDataKey: 'sharedMealBySharedBoard' },
      }),
      transformResponse: objectify,
      providesTags: (result, error, parentID) => [
        { type: 'DistilledSharedMealsByParent', id: parentID },
      ],
    }),
    getFullSharedMeal: builder.query<SharedMeal, string>({
      query: (id) => ({
        queryName: 'getSharedMeal',
        data: { id },
        queryType: QueryType.QUERY_BY_ID,
      }),
    }),
    getDistilledMealsByParents: builder.query<
      Record<string, Record<string, Meal>>,
      Array<string>
    >({
      query: (smorgBoardIDs) => ({
        queryName: 'listDistilledMealsByParent',
        data: { smorgBoardID: smorgBoardIDs },
        queryType: QueryType.SCAN_BY_IDS,
        appSyncParams: { responseDataKey: 'mealBySmorgBoard' },
      }),
      transformResponse: (data) => objectifyArrayOfArrays(data, 'smorgBoardID'),
      providesTags: (result, error, smorgBoardIDs) =>
        smorgBoardIDs.map((parentID) => ({
          type: 'DistilledMealsByParent',
          id: parentID,
        })),
      keepUnusedDataFor: 10 * 60,
    }),
    getDistilledSharedMealsByParents: builder.query<
      Record<string, Record<string, SharedMeal>>,
      Array<string>
    >({
      query: (sharedBoardIDs) => ({
        queryName: 'listDistilledSharedMealsByParent',
        data: { sharedBoardID: sharedBoardIDs },
        queryType: QueryType.SCAN_BY_IDS,
        appSyncParams: { responseDataKey: 'sharedMealBySharedBoard' },
      }),
      transformResponse: (data) =>
        objectifyArrayOfArrays(data, 'sharedBoardID'),
      providesTags: (result, error, sharedBoardIDs) =>
        sharedBoardIDs.map((parentID) => ({
          type: 'DistilledSharedMealsByParent',
          id: parentID,
        })),
      keepUnusedDataFor: 10 * 60,
    }),
    getFullMealsByParents: builder.query<
      Record<string, Record<string, Meal>>,
      Array<string>
    >({
      query: (smorgBoardIDs) => ({
        queryName: 'mealBySmorgBoard',
        data: { smorgBoardID: smorgBoardIDs },
        queryType: QueryType.SCAN_BY_IDS,
      }),
      transformResponse: (data) => objectifyArrayOfArrays(data, 'smorgBoardID'),
      providesTags: (result, error, smorgBoardIDs) =>
        smorgBoardIDs.map((parentID) => ({
          type: 'FullMealsByParent',
          id: parentID,
        })),
      keepUnusedDataFor: 10 * 60,
    }),
    getMyDayActionRecordsBySpaceMembership: builder.query<
      Array<UserMyDayActionRecord>,
      string
    >({
      query: (spaceMembershipID) => ({
        queryName: 'userMyDayActionRecordBySpaceMembership',
        data: { spaceMembershipID },
        queryType: QueryType.SCAN,
      }),
      transformResponse: (response) => {
        const sortedMyDayActionRecords = [...response];
        sortedMyDayActionRecords.sort((a, b) =>
          b.createdAt.localeCompare(a.createdAt),
        );
        return sortedMyDayActionRecords;
      },
      providesTags: [
        { type: 'UserMyDayActionRecordBySpaceMembership', id: 'LIST' },
      ],
    }),
    getMyDayActionRecordsByEnrollment: builder.query<
      Array<UserMyDayActionRecord>,
      string
    >({
      query: (userProgrammeEnrollmentID) => ({
        queryName: 'userMyDayActionRecordByUserProgrammeEnrollment',
        data: { userProgrammeEnrollmentID },
        queryType: QueryType.SCAN,
      }),
      transformResponse: (response) => {
        const sortedMyDayActionRecords = [...response];
        sortedMyDayActionRecords.sort((a, b) =>
          b.createdAt.localeCompare(a.createdAt),
        );
        return sortedMyDayActionRecords;
      },
      providesTags: [{ type: 'UserMyDayActionRecordByEnrollment', id: 'LIST' }],
    }),
    getSharedProgrammes: builder.query<Array<SharedProgramme>, string>({
      query: (spaceID) => ({
        queryName: 'sharedProgrammeBySpace',
        data: { spaceID },
        queryType: QueryType.SCAN,
      }),
      providesTags: (result) =>
        (result || []).map((sharedProgramme) => ({
          type: 'SharedProgramme',
          id: sharedProgramme.id,
        })),
    }),
    getSharedContentBoardsIAM: builder.query<
      Array<SharedContentBoard>,
      Array<string>
    >({
      // For the end user
      query: (sharedContentBoardIDs) => ({
        queryName: 'getSharedContentBoard',
        data: { id: sharedContentBoardIDs },
        queryType: QueryType.QUERY_BY_IDS,
        appSyncParams: { authMode: 'iam' },
      }),
    }),
    getSharedContentBoardIAM: builder.query<SharedContentBoard, string>({
      // For the end user
      query: (id) => ({
        queryName: 'getSharedContentBoard',
        data: { id },
        queryType: QueryType.QUERY_BY_ID,
        appSyncParams: { authMode: 'iam' },
      }),
    }),
    getSharedContentBoards: builder.query<
      Array<SharedContentBoard>,
      Array<string>
    >({
      // For the health pro
      query: (sharedContentBoardIDs) => ({
        queryName: 'getSharedContentBoard',
        data: { id: sharedContentBoardIDs },
        queryType: QueryType.QUERY_BY_IDS,
      }),
      providesTags: [{ type: 'SharedContentBoard', id: 'LIST' }],
    }),
    getSharedContentEntriesByParent: builder.query<
      Record<string, SharedContentEntry>,
      string
    >({
      query: (parentID) => ({
        queryName: 'sharedContentEntryByParent',
        data: { parentID },
        queryType: QueryType.SCAN,
      }),
      transformResponse: objectify,
      providesTags: (result, error, parentID) => [
        { type: 'SharedContentEntriesByParent', id: parentID },
      ],
    }),
    getSharedContentEntriesByParents: builder.query<
      Record<string, Record<string, SharedContentEntry>>,
      Array<string>
    >({
      query: (parentIDs) => ({
        queryName: 'sharedContentEntryByParent',
        data: { parentID: parentIDs },
        queryType: QueryType.SCAN_BY_IDS,
      }),
      transformResponse: (data) => objectifyArrayOfArrays(data, 'parentID'),
      keepUnusedDataFor: 10 * 60,
      providesTags: (result, error, parentIDs) =>
        parentIDs.map((parentID) => ({
          type: 'SharedContentEntriesByParent',
          id: parentID,
        })),
    }),
    getSharedContentEntry: builder.query<SharedContentEntry, string>({
      query: (id) => ({
        queryName: 'getSharedContentEntry',
        data: { id },
        queryType: QueryType.QUERY_BY_ID,
      }),
    }),
    getSmorgBoards: builder.query<
      Array<SmorgBoard>,
      OwnerAndGroups | null | undefined
    >({
      query: (ownerAndGroups) => {
        const subFilters: Record<string, any> = [];
        if (ownerAndGroups?.username) {
          subFilters.push({ owner: { beginsWith: ownerAndGroups.username } });
        }
        if ((ownerAndGroups?.groups || []).length > 0) {
          ownerAndGroups?.groups.forEach((group) => {
            subFilters.push({ groups: { contains: group } });
          });
        }
        const filter = {
          or: subFilters,
        };

        return {
          queryName: 'listSmorgBoards',
          data: { filter },
          queryType: QueryType.SCAN,
        };
      },
      providesTags: [{ type: 'SmorgBoard', id: 'LIST' }],
    }),
    getSmorgBoardsByIDs: builder.query<Array<SmorgBoard>, Array<string>>({
      query: (ids) => ({
        queryName: 'getSmorgBoard',
        data: { id: ids },
        queryType: QueryType.QUERY_BY_IDS,
      }),
      providesTags: (result, error, ids) =>
        ids.map((id) => ({
          type: 'HealthProBoard',
          id,
        })),
    }),
    listProductsBoard: builder.query<ProductsBoard, void>({
      query: () => ({
        queryName: 'listProductsBoards',
        data: {},
        queryType: QueryType.SCAN,
      }),
      transformResponse: (response) => response[0],
      providesTags: ['ProductsBoard'],
    }),
    getShoppingListsByProductsBoard: builder.query<
      Record<string, ShoppingList>,
      string
    >({
      query: (productsBoardID) => ({
        queryName: 'shoppingListByProductsBoard',
        data: { productsBoardID },
        queryType: QueryType.SCAN,
      }),
      transformResponse: objectify,
      providesTags: ['ShoppingList'],
    }),
    getMembershipTiersBySpace: builder.query<Array<MembershipTier>, string>({
      query: (spaceID) => ({
        queryName: 'membershipTierBySpace',
        data: { spaceID },
        queryType: QueryType.SCAN,
        appSyncParams: { authMode: 'iam' },
      }),
      transformResponse: (response) =>
        response.toSorted(
          (a: MembershipTier, b: MembershipTier) =>
            (a.rank || 0) - (b.rank || 0),
        ),
      providesTags: [{ type: 'MembershipTier', id: 'LIST' }],
    }),
    getSpacePrivateConfigBySpace: builder.query<SpacePrivateConfig, string>({
      query: (spaceID) => ({
        queryName: 'spacePrivateConfigBySpaceID',
        data: { spaceID },
        queryType: QueryType.SCAN,
      }),
      transformResponse: (response) => response[0],
      providesTags: ['SpacePrivateConfig'],
    }),
    getProgrammes: builder.query<Array<Programme>, void>({
      query: () => ({
        queryName: 'listProgrammes',
        data: {},
        queryType: QueryType.SCAN,
      }),
      providesTags: [{ type: 'Programme', id: 'LIST' }],
    }),
    getProgramme: builder.query<Programme, string>({
      query: (id) => ({
        queryName: 'getProgramme',
        data: { id },
        queryType: QueryType.QUERY_BY_ID,
      }),
    }),
    getGRCRecipesByIDs: builder.query<Record<string, GRCRecipe>, Array<string>>(
      {
        query: (grcRecipeIDs) => ({
          queryName: 'getGRCRecipe',
          data: { grcRecipeID: grcRecipeIDs },
          queryType: QueryType.QUERY_BY_IDS,
        }),
        transformResponse: (response) => {
          for (const grcRecipeFromBackend of response) {
            if (grcRecipeFromBackend && grcRecipeFromBackend.recipe) {
              grcRecipeFromBackend.recipe.ingredients.forEach(
                (ing: RecipeIngredient) => {
                  ing.id ||= uuidv4();
                },
              );
              grcRecipeFromBackend.recipe.preparations.forEach(
                (prep: RecipePreparation) => {
                  prep.id ||= uuidv4();
                },
              );
            }
          }
          return objectify(response, 'grcRecipeID');
        },
      },
    ),
    getGlobalTags: builder.query<Array<GlobalTag>, void>({
      query: () => ({
        queryName: 'listGlobalTags',
        data: {},
        queryType: QueryType.SCAN,
      }),
      transformResponse: (response) => {
        const MINIMUM_TAG_COUNT = 3;
        const globalTagsSorted = response
          .filter((t: GlobalTag) => t.count >= MINIMUM_TAG_COUNT)
          .sort((a: GlobalTag, b: GlobalTag) => b.count - a.count)
          .map((a: GlobalTag) => a.tag);
        return globalTagsSorted;
      },
    }),
    getSpaceIAPSubscriptions: builder.query<Space, string>({
      query: () => ({
        queryName: 'listSpaceIAPSubscriptions',
        data: {},
        queryType: QueryType.SCAN,
        appSyncParams: { authMode: 'iam' },
      }),
    }),
    getGRCRecipeCategories: builder.query<Array<GRCRecipeCategory>, string>({
      query: (spaceID) => ({
        // Not an actual AppSync paginated query, but a custom Lambda
        queryName: 'getGRCRecipeCategories',
        data: { spaceID },
        queryType: QueryType.QUERY_BY_ID,
      }),
    }),
    getGRCResult: builder.query<GRCResultPage, GetGRCResultsMutationVariables>({
      query: ({ spaceID, grcRecipeCategoryID, offset }) => ({
        // Not an actual AppSync paginated query, but a custom Lambda
        queryName: 'getGRCResults',
        data: { spaceID, grcRecipeCategoryID, offset },
        queryType: QueryType.QUERY_BY_ID,
      }),
      serializeQueryArgs: ({
        endpointName,
        queryArgs: { spaceID, grcRecipeCategoryID },
      }) => {
        return `${endpointName}-${spaceID}-${grcRecipeCategoryID}`;
      },
      merge: (currentResult, newPage) => {
        currentResult.data.push(...newPage.data);
        currentResult.nextOffset = newPage.nextOffset;
        currentResult.moreAvailable = newPage.moreAvailable;
        currentResult.estimatedTotalResults = newPage.estimatedTotalResults;
      },
      forceRefetch({ currentArg, previousArg }) {
        return Number(currentArg?.offset) > Number(previousArg?.offset);
      },
    }),
    adminListChatGroups: builder.query<Array<ChatGroup>, void>({
      query: () => ({
        // Not an actual AppSync paginated query, but a custom Lambda
        queryName: 'adminListChatGroups',
        data: {},
        queryType: QueryType.QUERY_BY_ID,
      }),
    }),
    // getSharedContentBoard: builder.query<SharedContentBoard, string>({
    //   query: (id) => ({
    //     queryName: 'getSharedContentBoard',
    //     data: { id },
    //     queryType: QueryType.QUERY_BY_ID,
    //   }),
    //   providesTags: (result, error, id) => [{ type: 'SharedContentBoard', id }],
    // }),

    // ---
    syncSpaceMembership: builder.mutation<
      SpaceMembership,
      SyncSpaceMembershipMutationVariables
    >({
      query: (args) => ({
        queryName: 'syncSpaceMembership',
        data: args,
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['SpaceMembership'],
      onQueryStarted: async (args, { dispatch, getState, queryFulfilled }) => {
        const patchResults = [];
        const getSpaceMembershipQueriesToInvalidate =
          appSyncGraphQLApi.util.selectInvalidatedBy(getState(), [
            'SpaceMembership',
          ]);

        for (const {
          endpointName,
          originalArgs,
        } of getSpaceMembershipQueriesToInvalidate) {
          const patchResult = dispatch(
            appSyncGraphQLApi.util.updateQueryData(
              'getSpaceMembershipsByMemberID',
              originalArgs,
              (draft) => {
                for (const spaceMembershipDraft of draft) {
                  // TODO check for ID
                  Object.assign(spaceMembershipDraft, args);
                }
              },
            ),
          );
          patchResults.push(patchResult);
        }

        try {
          await queryFulfilled;
        } catch {
          for (const patchResult of patchResults) {
            patchResult.undo();
          }
        }
      },
    }),
    addUserMyDayActionRecord: builder.mutation<
      UserMyDayActionRecord,
      CreateUserMyDayActionRecordInput
    >({
      query: (newRecord) => ({
        queryName: 'createUserMyDayActionRecord',
        data: { input: newRecord },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: [
        'UserMyDayActionRecordByEnrollment',
        'UserMyDayActionRecordBySpaceMembership',
      ],
    }),
    createMeal: builder.mutation<Meal, CreateMealInput>({
      query: (input) => ({
        queryName: 'createMeal',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { smorgBoardID }) => [
        { type: 'DistilledMealsByParent', id: smorgBoardID },
        { type: 'FullMealsByParent', id: smorgBoardID },
      ],
      onQueryStarted: async (
        { smorgBoardID },
        { dispatch, queryFulfilled },
      ) => {
        try {
          const { data: newMeal } = await queryFulfilled;
          // Apply pessimistic update to the cache for current user programme enrollments
          dispatch(
            appSyncGraphQLApi.util.updateQueryData(
              'getDistilledMealsByParent',
              smorgBoardID || '',
              (draft) => {
                Object.assign(
                  draft[newMeal.id],
                  distilledMealFromMeal(newMeal),
                );
              },
            ),
          );
          dispatch(
            appSyncGraphQLApi.util.updateQueryData(
              'getFullMealsByParent',
              smorgBoardID || '',
              (draft) => {
                Object.assign(draft[newMeal.id], newMeal);
              },
            ),
          );
        } catch {
          // If the mutation fails, we don't need to do anything here
          // The query will be refetched automatically
        }
      },
    }),
    updateMeal: builder.mutation<Meal, UpdateMealInput>({
      query: (updatedRecord) => ({
        queryName: 'updateMeal',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { id, smorgBoardID }) => [
        { type: 'DistilledMealsByParent', id: smorgBoardID || '' },
        { type: 'FullMealsByParent', id: smorgBoardID || '' },
        { type: 'FullMeal', id },
      ],
      onQueryStarted: async (
        { id, smorgBoardID, ...patch },
        { dispatch, queryFulfilled },
      ) => {
        const distilledMealsByParentPatchResult = dispatch(
          appSyncGraphQLApi.util.updateQueryData(
            'getDistilledMealsByParent',
            smorgBoardID || '',
            (draft) => {
              if (draft[id]) {
                Object.assign(draft[id], distilledMealFromMeal(patch));
              }
            },
          ),
        );
        const fullMealsByParentPatchResult = dispatch(
          appSyncGraphQLApi.util.updateQueryData(
            'getFullMealsByParent',
            smorgBoardID || '',
            (draft) => {
              if (draft[id]) {
                Object.assign(draft[id], patch);
              }
            },
          ),
        );
        const fullMealPatchResult = dispatch(
          appSyncGraphQLApi.util.updateQueryData(
            'getFullMeal',
            id || '',
            (draft) => {
              Object.assign(draft, patch);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          distilledMealsByParentPatchResult.undo();
          fullMealsByParentPatchResult.undo();
          fullMealPatchResult.undo();
        }
      },
    }),
    deleteMeal: builder.mutation<void, IDAndParentID>({
      query: ({ id }) => ({
        queryName: 'deleteMeal',
        data: { input: { id } },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { parentID }) => [
        { type: 'DistilledMealsByParent', id: parentID },
        { type: 'FullMealsByParent', id: parentID },
      ],
      onQueryStarted: async (
        { id, parentID: smorgBoardID },
        { dispatch, queryFulfilled },
      ) => {
        const distilledMealsByParentPatchResult = dispatch(
          appSyncGraphQLApi.util.updateQueryData(
            'getDistilledMealsByParent',
            smorgBoardID || '',
            (draft) => {
              delete draft[id];
            },
          ),
        );
        const fullMealsByParentPatchResult = dispatch(
          appSyncGraphQLApi.util.updateQueryData(
            'getFullMealsByParent',
            smorgBoardID || '',
            (draft) => {
              delete draft[id];
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          distilledMealsByParentPatchResult.undo();
          fullMealsByParentPatchResult.undo();
        }
      },
    }),
    createNote: builder.mutation<Note, CreateNoteInput>({
      query: (input) => ({
        queryName: 'createNote',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { parentID }) => [
        { type: 'NotesByParent', id: parentID },
      ],
    }),
    updateNote: builder.mutation<Note, UpdateNoteInput>({
      query: (updatedRecord) => ({
        queryName: 'updateNote',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { parentID }) => [
        { type: 'NotesByParent', id: parentID || '' },
      ],
    }),
    deleteNote: builder.mutation<void, IDAndParentID>({
      query: ({ id }) => ({
        queryName: 'deleteNote',
        data: { input: { id } },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { parentID }) => [
        { type: 'NotesByParent', id: parentID },
      ],
    }),
    createContentEntry: builder.mutation<ContentEntry, CreateContentEntryInput>(
      {
        query: (input) => ({
          queryName: 'createContentEntry',
          data: { input },
          queryType: QueryType.MUTATION,
        }),
        invalidatesTags: (result, error, { parentID }) => [
          { type: 'ContentEntriesByParent', id: parentID },
        ],
      },
    ),
    updateContentEntry: builder.mutation<ContentEntry, UpdateContentEntryInput>(
      {
        query: (updatedRecord) => ({
          queryName: 'updateContentEntry',
          data: { input: getInputForUpdate(updatedRecord) },
          queryType: QueryType.MUTATION,
        }),
        invalidatesTags: (result, error, { parentID }) => [
          { type: 'ContentEntriesByParent', id: parentID || '' },
        ],
      },
    ),
    deleteContentEntry: builder.mutation<void, IDAndParentID>({
      query: ({ id }) => ({
        queryName: 'deleteContentEntry',
        data: { input: { id } },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { parentID }) => [
        { type: 'ContentEntriesByParent', id: parentID },
      ],
    }),
    updateUserProfile: builder.mutation<UserProfile, UpdateUserProfileInput>({
      query: (updatedRecord) => ({
        queryName: 'updateUserProfile',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['UserProfile'],
    }),
    createSmorgBoard: builder.mutation<SmorgBoard, CreateSmorgBoardInput>({
      query: (input) => ({
        queryName: 'createSmorgBoard',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: () => [{ type: 'SmorgBoard', id: 'LIST' }],
      onQueryStarted: async (input, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data: newSmorgBoard } = await queryFulfilled;
          // Apply pessimistic update to the cache for current user programme enrollments
          const getSmorgBoardsQueriesToInvalidate =
            appSyncGraphQLApi.util.selectInvalidatedBy(getState(), [
              { type: 'SmorgBoard', id: 'LIST' },
            ]);

          for (const {
            endpointName,
            originalArgs,
          } of getSmorgBoardsQueriesToInvalidate) {
            if (endpointName === 'getSmorgBoards') {
              dispatch(
                appSyncGraphQLApi.util.updateQueryData(
                  'getSmorgBoards',
                  originalArgs,
                  (draft) => {
                    draft.push(newSmorgBoard);
                  },
                ),
              );
            }
          }
        } catch {
          // If the mutation fails, we don't need to do anything here
          // The query will be refetched automatically
        }
      },
    }),
    updateSmorgBoard: builder.mutation<SmorgBoard, UpdateSmorgBoardInput>({
      query: (updatedRecord) => ({
        queryName: 'updateSmorgBoard',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'SmorgBoard', id },
        { type: 'SmorgBoard', id: 'LIST' },
      ],
      onQueryStarted: async (
        _updatedRecord,
        { dispatch, getState, queryFulfilled },
      ) => {
        try {
          const { data: newUpdatedBoard } = await queryFulfilled;
          const getSmorgBoardsQueriesToInvalidate =
            appSyncGraphQLApi.util.selectInvalidatedBy(getState(), [
              { type: 'SmorgBoard', id: 'LIST' },
            ]);
          // BEGIN debug
          // const allArgs = appSyncGraphQLApi.util.selectCachedArgsForQuery(
          //   getState(),
          //   'getSmorgBoards',
          // );
          // console.log(JSON.stringify({ allArgs }));
          // END debug

          for (const {
            endpointName,
            originalArgs,
          } of getSmorgBoardsQueriesToInvalidate) {
            if (endpointName === 'getSmorgBoards') {
              // console.log(JSON.stringify({ originalArgs }));
              dispatch(
                appSyncGraphQLApi.util.updateQueryData(
                  'getSmorgBoards',
                  originalArgs,
                  (draft) => {
                    const index = draft.findIndex(
                      (smorgBoard) => smorgBoard.id === newUpdatedBoard.id,
                    );
                    if (index !== -1) {
                      console.log(
                        `Pessimistically updating SmorgBoard ${index}`,
                      );
                      Object.assign(draft[index], newUpdatedBoard);
                    }
                  },
                ),
              );
            }
          }
          dispatch(
            appSyncGraphQLApi.util.updateQueryData(
              'getSmorgBoard',
              newUpdatedBoard.id,
              (draft) => Object.assign(draft, newUpdatedBoard),
            ),
          );
        } catch {
          // If the mutation fails, we don't need to do anything here
          // The query will be refetched automatically
        }
      },
    }),
    updateSmorgBoardOptimistically: builder.mutation<
      SmorgBoard,
      UpdateSmorgBoardInput
    >({
      // For operations that require fast UI changes,
      // AND do not lead to data loss, e.g. dragging a card from one menu to another
      query: (updatedRecord) => ({
        queryName: 'updateSmorgBoard',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'SmorgBoard', id },
        { type: 'SmorgBoard', id: 'LIST' },
      ],
      onQueryStarted: async (
        updatedRecord,
        { dispatch, getState, queryFulfilled },
      ) => {
        const getSmorgBoardsQueriesToInvalidate =
          appSyncGraphQLApi.util.selectInvalidatedBy(getState(), [
            { type: 'SmorgBoard', id: 'LIST' },
          ]);
        // BEGIN debug
        // const allArgs = appSyncGraphQLApi.util.selectCachedArgsForQuery(
        //   getState(),
        //   'getSmorgBoards',
        // );
        // console.log(JSON.stringify({ allArgs }));
        // END debug

        const getSmorgBoardsPatchResults = [];
        for (const {
          endpointName,
          originalArgs,
        } of getSmorgBoardsQueriesToInvalidate) {
          if (endpointName === 'getSmorgBoards') {
            // console.log(JSON.stringify({ originalArgs }));
            getSmorgBoardsPatchResults.push(
              dispatch(
                appSyncGraphQLApi.util.updateQueryData(
                  'getSmorgBoards',
                  originalArgs,
                  (draft) => {
                    const index = draft.findIndex(
                      (smorgBoard) => smorgBoard.id === updatedRecord.id,
                    );
                    if (index !== -1) {
                      console.log(
                        `Optimistically updating SmorgBoard ${index}`,
                      );
                      Object.assign(
                        draft[index],
                        getInputForUpdate(updatedRecord),
                      );
                    }
                  },
                ),
              ),
            );
          }
        }
        const getSmorgBoardPatchResult = dispatch(
          appSyncGraphQLApi.util.updateQueryData(
            'getSmorgBoard',
            updatedRecord.id,
            (draft) => Object.assign(draft, getInputForUpdate(updatedRecord)),
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          getSmorgBoardPatchResult.undo();
          getSmorgBoardsPatchResults.forEach((patchResult) => {
            patchResult.undo();
          });
        }
      },
    }),
    deleteSmorgBoard: builder.mutation<void, string>({
      query: (id) => ({
        queryName: 'deleteSmorgBoard',
        data: { input: { id } },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'SmorgBoard', id },
        { type: 'SmorgBoard', id: 'LIST' },
      ],
    }),
    createDbPlannerWeek: builder.mutation<PlannerWeek, CreatePlannerWeekInput>({
      query: (input) => ({
        queryName: 'createPlannerWeek',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { startDate }) => [
        { type: 'DbPlannerWeekByStartDate', id: startDate },
      ],
    }),
    updateDbPlannerWeek: builder.mutation<PlannerWeek, UpdatePlannerWeekInput>({
      query: (updatedRecord) => ({
        queryName: 'updatePlannerWeek',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { startDate }) => [
        { type: 'DbPlannerWeekByStartDate', id: startDate || '' },
      ],
      onQueryStarted: async (
        { id, startDate },
        { dispatch, queryFulfilled },
      ) => {
        const safeStartDate = startDate || '';

        console.log(
          `updateDbPlannerWeek pessimistically onQueryStarted ${id} ${safeStartDate}`,
        );
        try {
          const { data: newUpdatedDbPlannerWeek } = await queryFulfilled;

          dispatch(
            appSyncGraphQLApi.util.updateQueryData(
              'getDbPlannerWeek',
              safeStartDate,
              (draft) => {
                Object.assign(draft, newUpdatedDbPlannerWeek);
              },
            ),
          );

          dispatch(
            appSyncGraphQLApi.util.updateQueryData(
              'getDbPlannerWeekByID',
              id,
              (draft) => {
                Object.assign(draft, newUpdatedDbPlannerWeek);
              },
            ),
          );
        } catch {
          // If the mutation fails, we don't need to do anything here
          // The query will be refetched automatically
        }
      },
    }),
    updateDbPlannerWeekOptimistically: builder.mutation<
      PlannerWeek,
      UpdatePlannerWeekInput
    >({
      query: (updatedRecord) => ({
        queryName: 'updatePlannerWeek',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { startDate }) => [
        { type: 'DbPlannerWeekByStartDate', id: startDate || '' },
      ],
      onQueryStarted: async (
        { id, startDate, ...patch },
        { dispatch, queryFulfilled },
      ) => {
        const safeStartDate = startDate || '';

        console.log(
          `updateDbPlannerWeek optimistically onQueryStarted ${id} ${safeStartDate}`,
        );

        const getDbPlannerWeekByStartDatePatchResult = dispatch(
          appSyncGraphQLApi.util.updateQueryData(
            'getDbPlannerWeek',
            safeStartDate,
            (draft) => {
              Object.assign(draft, patch);
            },
          ),
        );

        const getDbPlannerWeekByIDPatchResult = dispatch(
          appSyncGraphQLApi.util.updateQueryData(
            'getDbPlannerWeekByID',
            id,
            (draft) => {
              Object.assign(draft, patch);
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          getDbPlannerWeekByStartDatePatchResult.undo();
          getDbPlannerWeekByIDPatchResult.undo();
        }
      },
    }),
    createMembershipTier: builder.mutation<
      MembershipTier,
      CreateMembershipTierInput
    >({
      query: (input) => ({
        queryName: 'createMembershipTier',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['MembershipTier'],
    }),
    updateMembershipTier: builder.mutation<
      MembershipTier,
      UpdateMembershipTierInput
    >({
      query: (updatedRecord) => ({
        queryName: 'updateMembershipTier',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['MembershipTier'],
    }),
    deleteMembershipTier: builder.mutation<void, string>({
      query: (id) => ({
        queryName: 'deleteMembershipTier',
        data: { input: { id } },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['MembershipTier'],
    }),
    activateMembershipTier: builder.mutation<
      MembershipTier,
      ActivateMembershipTierMutationVariables
    >({
      query: (membershipTierID) => ({
        queryName: 'activateMembershipTier',
        data: { membershipTierID },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['MembershipTier'],
    }),
    createSpace: builder.mutation<Space, CreateSpaceInput>({
      query: (input) => ({
        queryName: 'createSpace',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['Space'],
    }),
    updateSpace: builder.mutation<Space, UpdateSpaceInput>({
      query: (updatedRecord) => ({
        queryName: 'updateSpace',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['Space'],
    }),
    updateSharedContentEntry: builder.mutation<
      SharedContentEntry,
      UpdateSharedContentEntryInput
    >({
      query: (updatedRecord) => ({
        queryName: 'updateSharedContentEntry',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { parentID }) => [
        { type: 'SharedContentEntriesByParent', id: parentID || '' },
      ],
      onQueryStarted: async (
        { id, parentID, ...patch },
        { dispatch, queryFulfilled },
      ) => {
        const getSharedContentEntriesByParentPatchResult = dispatch(
          appSyncGraphQLApi.util.updateQueryData(
            'getSharedContentEntriesByParent',
            parentID || '',
            (draft) => {
              if (draft[id]) {
                Object.assign(draft[id], patch);
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          getSharedContentEntriesByParentPatchResult.undo();
        }
      },
    }),
    createSpacePrivateConfig: builder.mutation<
      SpacePrivateConfig,
      CreateSpacePrivateConfigInput
    >({
      query: (input) => ({
        queryName: 'createSpacePrivateConfig',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['SpacePrivateConfig'],
    }),
    updateSpacePrivateConfig: builder.mutation<
      SpacePrivateConfig,
      UpdateSpacePrivateConfigInput
    >({
      query: (updatedRecord) => ({
        queryName: 'updateSpacePrivateConfig',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['SpacePrivateConfig'],
    }),
    createUserProfile: builder.mutation<UserProfile, CreateUserProfileInput>({
      query: (input) => ({
        queryName: 'createUserProfile',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['UserProfile'],
    }),
    createProductsBoard: builder.mutation<
      ProductsBoard,
      CreateProductsBoardInput
    >({
      query: (input) => ({
        queryName: 'createProductsBoard',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['ProductsBoard'],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data: newProductsBoard } = await queryFulfilled;
          await dispatch(
            appSyncGraphQLApi.util.upsertQueryData(
              'listProductsBoard',
              undefined,
              newProductsBoard,
              // Work around RTK Query type bug?
              // [newProductsBoard] as any as ProductsBoard,
            ),
          );
        } catch {
          // If the mutation fails, we don't need to do anything
        }
      },
    }),
    updateProductsBoard: builder.mutation<
      ProductsBoard,
      UpdateProductsBoardInput
    >({
      query: (updatedRecord) => ({
        queryName: 'updateProductsBoard',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['ProductsBoard'],
      onQueryStarted: async (
        { id, ...patch },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          appSyncGraphQLApi.util.updateQueryData(
            'listProductsBoard',
            undefined,
            (draft) => {
              Object.assign(draft, patch);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    createShoppingList: builder.mutation<ShoppingList, CreateShoppingListInput>(
      {
        query: (input) => ({
          queryName: 'createShoppingList',
          data: { input },
          queryType: QueryType.MUTATION,
        }),
        invalidatesTags: ['ShoppingList'],
        onQueryStarted: async (
          { productsBoardID },
          { dispatch, queryFulfilled },
        ) => {
          try {
            const { data: newShoppingList } = await queryFulfilled;

            dispatch(
              appSyncGraphQLApi.util.updateQueryData(
                'getShoppingListsByProductsBoard',
                productsBoardID,
                (draft) => {
                  Object.assign(draft[newShoppingList.id], newShoppingList);
                },
              ),
            );
          } catch {
            // If the mutation fails, we don't need to do anything
          }
        },
      },
    ),
    updateShoppingList: builder.mutation<ShoppingList, UpdateShoppingListInput>(
      {
        query: (updatedRecord) => ({
          queryName: 'updateShoppingList',
          data: { input: getInputForUpdate(updatedRecord) },
          queryType: QueryType.MUTATION,
        }),
        invalidatesTags: ['ShoppingList'],
        onQueryStarted: async (
          { id, productsBoardID, ...patch },
          { dispatch, queryFulfilled },
        ) => {
          const patchResult = dispatch(
            appSyncGraphQLApi.util.updateQueryData(
              'getShoppingListsByProductsBoard',
              productsBoardID || '',
              (draft) => {
                if (draft[id]) {
                  Object.assign(draft[id], patch);
                }
              },
            ),
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      },
    ),
    updateShoppingListWithoutRefetch: builder.mutation<
      ShoppingList,
      UpdateShoppingListInput
    >({
      query: (updatedRecord) => ({
        queryName: 'updateShoppingList',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      // Same as updateShoppingList, with optimistic update, but does not invalidate the ShoppingList tag
      onQueryStarted: async (
        { id, productsBoardID, ...patch },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          appSyncGraphQLApi.util.updateQueryData(
            'getShoppingListsByProductsBoard',
            productsBoardID || '',
            (draft) => {
              if (draft[id]) {
                Object.assign(draft[id], patch);
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    deleteShoppingList: builder.mutation<void, string>({
      query: (id) => ({
        queryName: 'deleteShoppingList',
        data: { input: { id } },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['ShoppingList'],
      onQueryStarted: async (id, { dispatch, getState, queryFulfilled }) => {
        const queriesToInvalidate = appSyncGraphQLApi.util.selectInvalidatedBy(
          getState(),
          ['ShoppingList'],
        );

        const patchResults = [];
        for (const { endpointName, originalArgs } of queriesToInvalidate) {
          if (endpointName === 'getShoppingListsByProductsBoard') {
            patchResults.push(
              dispatch(
                appSyncGraphQLApi.util.updateQueryData(
                  'getShoppingListsByProductsBoard',
                  originalArgs,
                  (draft) => {
                    delete draft[id];
                  },
                ),
              ),
            );
          }
        }
        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patchResult) => {
            patchResult.undo();
          });
        }
      },
    }),
    createUserProgrammeEnrollment: builder.mutation<
      UserProgrammeEnrollment,
      CreateUserProgrammeEnrollmentInput
    >({
      query: (input) => ({
        queryName: 'createUserProgrammeEnrollment',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['UserProgrammeEnrollment'],
      onQueryStarted: async (input, { dispatch, queryFulfilled }) => {
        try {
          const { data: newEnrollment } = await queryFulfilled;
          // console.log(JSON.stringify({ newEnrollment }));
          // Apply pessimistic update to the cache for current user programme enrollments
          dispatch(
            appSyncGraphQLApi.util.updateQueryData(
              'getUserProgrammeEnrollments',
              undefined,
              (draft) => {
                draft.unshift(newEnrollment);
              },
            ),
          );
        } catch {
          // If the mutation fails, we don't need to do anything here
          // The query will be refetched automatically
        }
      },
    }),
    updateUserProgrammeEnrollment: builder.mutation<
      UserProgrammeEnrollment,
      UpdateUserProgrammeEnrollmentInput
    >({
      query: (updatedRecord) => ({
        queryName: 'updateUserProgrammeEnrollment',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['UserProgrammeEnrollment'],
      onQueryStarted: async (updatedRecord, { dispatch, queryFulfilled }) => {
        const getUserProgrammeEnrollmentsPatchResult = dispatch(
          appSyncGraphQLApi.util.updateQueryData(
            'getUserProgrammeEnrollments',
            undefined,
            (draft) => {
              const index = draft.findIndex(
                (enrollment) => enrollment.id === updatedRecord.id,
              );
              if (index !== -1) {
                Object.assign(draft[index], getInputForUpdate(updatedRecord));
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          getUserProgrammeEnrollmentsPatchResult.undo();
        }
      },
    }),
    createProgramme: builder.mutation<Programme, CreateProgrammeInput>({
      query: (input) => ({
        queryName: 'createProgramme',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: [{ type: 'Programme', id: 'LIST' }],
    }),
    updateProgramme: builder.mutation<Programme, UpdateProgrammeInput>({
      query: (updatedRecord) => ({
        queryName: 'updateProgramme',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: () => [{ type: 'Programme', id: 'LIST' }],
      onQueryStarted: async (
        { id, ...patch },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          appSyncGraphQLApi.util.updateQueryData(
            'getProgrammes',
            undefined,
            (draft) => {
              for (const programme of draft) {
                if (programme.id === id) {
                  Object.assign(programme, patch);
                }
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    deleteProgramme: builder.mutation<void, string>({
      query: (id) => ({
        queryName: 'deleteProgramme',
        data: { input: { id } },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: () => [{ type: 'Programme', id: 'LIST' }],
    }),
    createSharedContentBoard: builder.mutation<
      SharedContentBoard,
      CreateSharedContentBoardInput
    >({
      query: (input) => ({
        queryName: 'createSharedContentBoard',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['SharedContentBoard'],
    }),
    updateSharedContentBoard: builder.mutation<
      SharedContentBoard,
      UpdateSharedContentBoardInput
    >({
      query: (updatedRecord) => ({
        queryName: 'updateSharedContentBoard',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['SharedContentBoard'],
      onQueryStarted: async (
        updatedRecord,
        { dispatch, getState, queryFulfilled },
      ) => {
        const queriesToInvalidate = appSyncGraphQLApi.util.selectInvalidatedBy(
          getState(),
          ['SharedContentBoard'],
        );

        const patchResults = [];
        for (const { endpointName, originalArgs } of queriesToInvalidate) {
          if (endpointName === 'getSharedContentBoards') {
            patchResults.push(
              dispatch(
                appSyncGraphQLApi.util.updateQueryData(
                  'getSharedContentBoards',
                  originalArgs,
                  (draft) => {
                    const index = draft.findIndex(
                      (sharedContentBoard) =>
                        sharedContentBoard.id === updatedRecord.id,
                    );
                    console.log(
                      `Optimistically updating sharedContentBoard ${index}`,
                    );
                    if (index !== -1) {
                      Object.assign(
                        draft[index],
                        getInputForUpdate(updatedRecord),
                      );
                    }
                  },
                ),
              ),
            );
          }
        }
        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patchResult) => {
            patchResult.undo();
          });
        }
      },
    }),
    createSharedContentEntry: builder.mutation<
      SharedContentEntry,
      CreateSharedContentEntryInput
    >({
      query: (input) => ({
        queryName: 'createSharedContentEntry',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { parentID }) => [
        { type: 'SharedContentEntriesByParent', id: parentID },
      ],
    }),
    deleteSharedContentEntry: builder.mutation<void, IDAndParentID>({
      query: ({ id }) => ({
        queryName: 'deleteSharedContentEntry',
        data: { input: { id } },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { parentID }) => [
        { type: 'SharedContentEntriesByParent', id: parentID },
      ],
    }),
    createSharedProgramme: builder.mutation<
      SharedProgramme,
      CreateSharedProgrammeInput
    >({
      query: (input) => ({
        queryName: 'createSharedProgramme',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: ['SharedProgramme'],
    }),
    updateSharedProgramme: builder.mutation<
      SharedProgramme,
      UpdateSharedProgrammeInput
    >({
      query: (updatedRecord) => ({
        queryName: 'updateSharedProgramme',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'SharedProgramme', id },
      ],
    }),
    deleteSharedProgramme: builder.mutation<void, string>({
      query: (id) => ({
        queryName: 'deleteSharedProgramme',
        data: { input: { id } },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, id) => [{ type: 'SharedProgramme', id }],
    }),
    createSharedBoard: builder.mutation<SharedBoard, CreateSharedBoardInput>({
      query: (input) => ({
        queryName: 'createSharedBoard',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
    }),
    updateSharedBoard: builder.mutation<SharedBoard, UpdateSharedBoardInput>({
      query: (updatedRecord) => ({
        queryName: 'updateSharedBoard',
        data: { input: getInputForUpdate(updatedRecord) },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'SharedBoard', id }],
    }),
    createSharedMeal: builder.mutation<SharedMeal, CreateSharedMealInput>({
      query: (input) => ({
        queryName: 'createSharedMeal',
        data: { input },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { sharedBoardID }) => [
        { type: 'DistilledSharedMealsByParent', id: sharedBoardID },
      ],
    }),
    deleteSharedMeal: builder.mutation<void, IDAndParentID>({
      query: ({ id }) => ({
        queryName: 'deleteSharedMeal',
        data: { input: { id } },
        queryType: QueryType.MUTATION,
      }),
      invalidatesTags: (result, error, { parentID }) => [
        { type: 'DistilledSharedMealsByParent', id: parentID },
      ],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetSpaceMembershipsByMemberIDQuery,
  useGetDbPlannerWeekQuery,
  useLazyGetDbPlannerWeekQuery,
  useLazyGetDbPlannerWeekByIDQuery,
  useGetDistilledMealsByParentQuery,
  useLazyGetDistilledMealsByParentQuery,
  useLazyGetFullMealsByParentQuery,
  useGetFullMealQuery,
  useLazyGetFullMealQuery,
  useGetNotesByParentQuery,
  useLazyGetNotesByParentQuery,
  useGetContentEntriesByParentQuery,
  useLazyGetContentEntryQuery,
  useLazyGetContentEntriesByParentQuery,
  useGetSharedProgrammeQuery,
  useGetPublicSpaceQuery,
  useLazyGetPublicSpaceQuery,
  useGetSpaceQuery,
  useGetSmorgBoardQuery,
  useGetSmorgBoardsByIDsQuery,
  useLazyGetSmorgBoardQuery,
  useGetSharedBoardQuery,
  useLazyGetSharedBoardQuery,
  useGetSharedBoardsByIDsQuery,
  useLazyGetDistilledSharedMealsByParentQuery,
  useGetDistilledSharedMealsByParentQuery,
  useGetFullSharedMealQuery,
  useLazyGetFullSharedMealQuery,
  useGetDistilledMealsByParentsQuery,
  useGetFullMealsByParentsQuery,
  useLazyGetFullMealsByParentsQuery,
  useGetDistilledSharedMealsByParentsQuery,
  useGetMyDayActionRecordsBySpaceMembershipQuery,
  useGetMyDayActionRecordsByEnrollmentQuery,
  useGetSharedProgrammesQuery,
  useGetSharedContentBoardsIAMQuery,
  useLazyGetSharedContentBoardIAMQuery,
  useGetSharedContentBoardsQuery,
  useGetSharedContentEntriesByParentQuery,
  useGetSharedContentEntriesByParentsQuery,
  useLazyGetSharedContentEntryQuery,
  useGetSmorgBoardsQuery,
  useListProductsBoardQuery,
  useLazyListProductsBoardQuery,
  useGetShoppingListsByProductsBoardQuery,
  useGetMembershipTiersBySpaceQuery,
  useGetSpacePrivateConfigBySpaceQuery,
  useGetProgrammesQuery,
  useLazyGetProgrammesQuery,
  useLazyGetProgrammeQuery,
  useGetGRCRecipesByIDsQuery,
  useLazyGetGRCRecipesByIDsQuery,
  useGetGlobalTagsQuery,
  useGetSpaceIAPSubscriptionsQuery,
  useGetGRCRecipeCategoriesQuery,
  useGetGRCResultQuery,
  useAdminListChatGroupsQuery,
  // ---
  useSyncSpaceMembershipMutation,
  useAddUserMyDayActionRecordMutation,
  useCreateMealMutation,
  useUpdateMealMutation,
  useDeleteMealMutation,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
  useCreateContentEntryMutation,
  useUpdateContentEntryMutation,
  useDeleteContentEntryMutation,
  useUpdateUserProfileMutation,
  useCreateSmorgBoardMutation,
  useUpdateSmorgBoardMutation,
  useUpdateSmorgBoardOptimisticallyMutation,
  useDeleteSmorgBoardMutation,
  useCreateDbPlannerWeekMutation,
  useUpdateDbPlannerWeekMutation,
  useUpdateDbPlannerWeekOptimisticallyMutation,
  useCreateMembershipTierMutation,
  useUpdateMembershipTierMutation,
  useDeleteMembershipTierMutation,
  useActivateMembershipTierMutation,
  useCreateSpaceMutation,
  useUpdateSpaceMutation,
  useUpdateSharedContentEntryMutation,
  useCreateSpacePrivateConfigMutation,
  useUpdateSpacePrivateConfigMutation,
  useCreateUserProfileMutation,
  useCreateProductsBoardMutation,
  useUpdateProductsBoardMutation,
  useCreateShoppingListMutation,
  useUpdateShoppingListMutation,
  useUpdateShoppingListWithoutRefetchMutation,
  useDeleteShoppingListMutation,
  useCreateUserProgrammeEnrollmentMutation,
  useUpdateUserProgrammeEnrollmentMutation,
  useCreateProgrammeMutation,
  useUpdateProgrammeMutation,
  useDeleteProgrammeMutation,
  useCreateSharedContentBoardMutation,
  useUpdateSharedContentBoardMutation,
  useCreateSharedContentEntryMutation,
  useDeleteSharedContentEntryMutation,
  useCreateSharedProgrammeMutation,
  useUpdateSharedProgrammeMutation,
  useDeleteSharedProgrammeMutation,
  useCreateSharedBoardMutation,
  useUpdateSharedBoardMutation,
  useCreateSharedMealMutation,
  useDeleteSharedMealMutation,
} = appSyncGraphQLApi;
